// apps/landing-page/src/components/search/searchRows.ts
// Turns the catalog payload and a query into the flat rows the dialog renders.
import type { ProductCatalog } from '@/lib/api';
import { searchItems, type SearchDoc } from '@/lib/search';

export const MAX_PER_GROUP = 5;
// Events that can still be opened, as on the events hub. COMPLETED and CANCELLED are history.
const OPEN_EVENT_STATUSES = new Set(['SCHEDULED', 'REGISTRATION_CLOSED', 'LIVE', 'PAUSED']);

export type GroupKey = 'modules' | 'bundles' | 'courses' | 'events' | 'posts' | 'exams' | 'all';

/** Display order of the headed groups. 'all' is the trailing see-all row and has no heading. */
export const GROUP_ORDER: Exclude<GroupKey, 'all'>[] = ['exams', 'modules', 'bundles', 'courses', 'events', 'posts'];

export interface SearchRow {
  id: string;
  group: GroupKey;
  title: string;
  context: string;
  href: string;
  /** First exam family of the item, for the icon and its colour. */
  familySlug?: string;
}

// Same call shape as next-intl's t(), without importing its generic types.
export type Translate = (key: string, values?: Record<string, string | number>) => string;

const rupiah = (amount: number) => `Rp ${amount.toLocaleString('id-ID')}`;
const join = (parts: (string | null | undefined | false)[]) => parts.filter(Boolean).join(' · ');
const familyNames = (types: { name: string }[]) => types.map((e) => e.name);

function formatDuration(mins: number, t: Translate): string {
  if (mins < 60) return t('minutes', { count: mins });
  return t('hours', { count: (mins / 60).toLocaleString('id-ID', { maximumFractionDigits: 1 }) });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

export const searchPageHref = (query: string) => `/cari?q=${encodeURIComponent(query)}`;

/** The row that sends the query to the full results page. */
export function seeAllRow(query: string, t: Translate): SearchRow {
  return { id: 'all', group: 'all', title: t('seeAll', { query }), context: '', href: searchPageHref(query) };
}

/** Matches in display order, at most `limit` per group (the results page passes Infinity). */
export function buildRows(
  products: ProductCatalog,
  query: string,
  { coursesGated, t, limit = MAX_PER_GROUP }: { coursesGated: boolean; t: Translate; limit?: number },
): SearchRow[] {
  const top = <T>(items: T[], toDoc: (item: T) => SearchDoc) =>
    searchItems(items, query, toDoc).slice(0, limit);

  const modules = top(products.modules, (m) => ({
    title: m.title,
    text: m.description,
    families: familyNames(m.examTypes),
  })).map<SearchRow>((m) => ({
    id: `module-${m.id}`,
    group: 'modules',
    title: m.title,
    href: `/tests/${m.slug}`,
    familySlug: m.examTypes[0]?.slug,
    // `price` is the effective price; `promoPrice` is raw and ignores the promo window.
    context: join([m.examTypes[0]?.name, m.isFree ? t('free') : rupiah(m.price)]),
  }));

  const bundles = top(products.bundles, (b) => ({
    title: b.title,
    text: b.description,
    families: familyNames(b.examTypes),
  })).map<SearchRow>((b) => ({
    id: `bundle-${b.id}`,
    group: 'bundles',
    title: b.title,
    href: `/tests/bundle/${b.slug}`,
    familySlug: b.examTypes[0]?.slug,
    context: join([b.examTypes[0]?.name, t('moduleCount', { count: b.moduleCount }), rupiah(b.price)]),
  }));

  const courses = coursesGated
    ? []
    : top(products.courses, (c) => ({
        title: c.title,
        text: c.shortDescription ?? c.description,
        families: familyNames(c.examTypes),
      })).map<SearchRow>((c) => ({
        id: `course-${c.id}`,
        group: 'courses',
        title: c.title,
        href: `/courses/${c.slug}`,
        familySlug: c.examTypes[0]?.slug,
        context: join([
          c.examTypes[0]?.name,
          c.isFree ? t('free') : rupiah(c.promoPrice ?? c.basePrice ?? 0),
          c.totalDurationMins > 0 && formatDuration(c.totalDurationMins, t),
        ]),
      }));

  const events = top(
    products.liveEvents.filter((e) => OPEN_EVENT_STATUSES.has(e.status)),
    (e) => ({ title: e.title, text: e.description }),
  ).map<SearchRow>((e) => ({
    id: `event-${e.id}`,
    group: 'events',
    title: e.title,
    href: `/events/${e.slug}`,
    // No pricing object means we do not know the price, so say nothing rather than "Gratis".
    context: join([
      e.status === 'LIVE' ? t('liveNow') : formatDate(e.startAt),
      e.pricing && (e.pricing.effectivePrice === 0 ? t('free') : rupiah(e.pricing.effectivePrice)),
    ]),
  }));

  const posts = top(products.blogPosts, (p) => ({
    title: p.title,
    text: p.excerpt,
    families: p.category ? [p.category.name] : [],
  })).map<SearchRow>((p) => ({
    id: `post-${p.id}`,
    group: 'posts',
    title: p.title,
    href: `/blog/${p.slug}`,
    context: join([p.category?.name, t('readingTime', { count: p.readingTimeMins })]),
  }));

  return [...modules, ...bundles, ...courses, ...events, ...posts];
}
